/**
 * /cricket/disputed-balls — ops review queue for canonical balls
 * where scorer sessions disagreed.
 *
 * Staff-only. Each row shows the variants from each session; ops pick
 * the canonical answer.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import api from "../../api/axios";

function VariantTable({ variants }) {
  return (
    <table className="w-full text-xs mt-2">
      <thead className="text-text-secondary uppercase tracking-wider">
        <tr>
          <th className="text-left py-1">Scorer</th>
          <th className="text-left py-1">Role</th>
          <th className="text-left py-1">Runs</th>
          <th className="text-left py-1">Wicket</th>
          <th className="text-left py-1">Intent</th>
        </tr>
      </thead>
      <tbody>
        {(variants || []).map((v, i) => (
          <tr key={i} className="border-t border-border-default">
            <td className="py-1 font-mono">{(v.scorer_id || "anon").slice(0, 8)}</td>
            <td className="py-1">
              {v.scorer_role === "staff" ? (
                <span className="text-emerald-400 font-semibold">staff</span>
              ) : (
                <span className="opacity-60">crowd</span>
              )}
            </td>
            <td className="py-1">{v.runs}</td>
            <td className="py-1">{v.wicket || "—"}</td>
            <td className="py-1">{v.intent || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Row({ ball, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);

  const resolve = async (variant) => {
    setBusy(true);
    try {
      await api.post(`/api/admin/disputed-balls/${ball.id}/resolve/`, {
        canonical_runs:   variant.runs,
        canonical_wicket: variant.wicket || "",
        canonical_intent: variant.intent || "",
      });
      onResolved(ball.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-bg-primary border border-border-default rounded-xl p-4 mb-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            {ball.match_label}
          </div>
          <div className="text-xs text-text-secondary">Over {ball.over} · seq {ball.seq} · {(ball.confidence * 100).toFixed(0)}% confidence</div>
        </div>
      </div>
      <VariantTable variants={ball.variants} />
      <div className="mt-3 flex flex-wrap gap-2">
        {(ball.variants || []).map((v, i) => (
          <button
            key={i}
            onClick={() => { setPicked(i); resolve(v); }}
            disabled={busy}
            className={`text-xs px-2 py-1.5 rounded border ${
              picked === i
                ? "bg-emerald-500/20 border-emerald-500/40"
                : "bg-bg-secondary border-border-default hover:border-button-primary"
            } disabled:opacity-50`}
          >
            {busy && picked === i ? <Loader2 className="w-3 h-3 inline animate-spin" /> : null}
            {v.scorer_role === "staff" ? "✓ " : ""}Use scorer {(v.scorer_id || "anon").slice(0, 6)}: {v.runs}r {v.wicket || ""}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DisputedBalls() {
  const [balls, setBalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const reload = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/api/admin/disputed-balls/");
      setBalls(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load.");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const onResolved = (id) => setBalls((b) => b.filter((x) => x.id !== id));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" /> Disputed canonical balls
      </h2>
      <p className="text-sm text-text-secondary mb-5">
        Balls where scorer sessions couldn&apos;t agree. Pick the correct version to resolve.
      </p>

      {loading ? (
        <div className="text-text-secondary text-sm">Loading…</div>
      ) : err ? (
        <div className="bg-button-primary/10 border border-button-primary/30 rounded-lg px-3.5 py-2.5 text-sm text-button-primary">{err}</div>
      ) : balls.length === 0 ? (
        <div className="text-sm text-emerald-400 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> Nothing to review. All canonical balls have consensus.
        </div>
      ) : (
        balls.map((b) => <Row key={b.id} ball={b} onResolved={onResolved} />)
      )}
    </div>
  );
}
